import { useState, useRef } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import Popover from './Popover'

interface MultiSelectProps {
  values: string[]
  onChange: (values: string[]) => void
  options: readonly (string | readonly [string, string])[]
  placeholder: string
  className?: string
  searchable?: boolean
}

export default function MultiSelect({ values, onChange, options, placeholder, className = '', searchable = false }: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const toggle = (val: string) => {
    onChange(values.includes(val) ? values.filter(v => v !== val) : [...values, val])
  }

  // Show the option's display label (not its raw value) in the collapsed button,
  // so pairs like ['06','Jun'] read as "Jun". Falls back to the value itself for
  // plain string options.
  const labelFor = (val: string) => {
    const opt = options.find(o => (Array.isArray(o) ? o[0] : o) === val)
    return opt ? (Array.isArray(opt) ? opt[1] : opt) : val
  }
  const label =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? labelFor(values[0])
        : `${labelFor(values[0])} +${values.length - 1}`

  const filtered = searchable && query
    ? options.filter(opt => {
        const lbl = Array.isArray(opt) ? opt[1] : opt
        return lbl.toLowerCase().includes(query.toLowerCase())
      })
    : options

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { setOpen(!open); setQuery('') }}
        onKeyDown={e => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm font-medium cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-danone-cyan transition-colors ${
          values.length > 0
            ? 'border-danone-cyan bg-cyan-50 text-danone-blue'
            : 'border-slate-200 bg-slate-50 text-slate-700'
        }`}
      >
        <span>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <Popover open={open} anchorRef={ref} onClose={() => { setOpen(false); setQuery('') }} className="min-w-[180px]">
        {searchable && (
          <div className="p-2 border-b border-slate-100 flex-shrink-0">
            <input
              autoFocus
              data-popover-search
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search..."
              aria-label="Search options"
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:border-danone-cyan"
            />
          </div>
        )}
        <div role="listbox" aria-multiselectable="true" className="py-1 max-h-72 min-h-0 overflow-y-auto">
          {filtered.map(opt => {
            const [val, lbl] = Array.isArray(opt) ? opt : [opt, opt]
            const selected = values.includes(val)
            return (
              <button
                key={val}
                type="button"
                role="option"
                aria-selected={selected}
                data-option
                onClick={() => toggle(val)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none transition-colors"
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selected ? 'bg-danone-cyan border-danone-cyan' : 'border-slate-300'}`}>
                  {selected && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                {lbl}
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">No results</div>
          )}
        </div>
      </Popover>
    </div>
  )
}
